import { Box, Flex, Text } from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';

import { CustomOptionSelectorSelectComponent } from '../CustomOptionSelector';

interface TablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const navBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: '1px solid',
  borderColor: '#D0D5DD',
  borderRadius: '4px',
  background: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#344054',
  _disabled: { opacity: 0.4, cursor: 'not-allowed' },
  _hover: { background: '#F9FAFB' },
};

export default function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}: TablePaginationProps) {
  const pageCount = Math.ceil(total / pageSize);
  const startItem =
    total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total);
  const endItem = Math.min(page * pageSize, total);

  return (
    <Box
      mt={4}
      px={4}
      pt={3}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Flex alignItems="center" gap={2}>
        <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
          Rows per page
        </Text>
        <CustomOptionSelectorSelectComponent
          defaultValue={pageSize}
          options={pageSizeOptions}
          labels={pageSizeOptions.map(String)}
          size="large-unrounded"
          selectedOption={pageSize}
          setSelectedOption={(option) => {
            onPageSizeChange(Number(option));
            onPageChange(1);
          }}
        />
      </Flex>

      <Flex alignItems="center" gap={3}>
        <Text fontSize="sm" color="#00111A" whiteSpace="nowrap">
          {startItem}-{endItem}{' '}
          <Text as="span" color="#A3ABAD">
            of
          </Text>{' '}
          {total}
        </Text>
        <Flex
          alignItems="center"
          gap={1}
          sx={{
            '.pagination-nav': {
              display: 'flex',
              listStyle: 'none',
              gap: '4px',
            },
            '.nav-btn': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              border: '1px solid #D0D5DD',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#344054',
            },
            '.nav-btn:hover': { background: '#F9FAFB' },
            '.hidden-page': { display: 'none' },
            '.hidden-break': { display: 'none' },
            '.nav-disabled .nav-btn': {
              opacity: 0.4,
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          }}
        >
          <Box
            as="button"
            sx={navBtnStyle}
            onClick={() => onPageChange(1)}
            disabled={page === 1}
          >
            «
          </Box>
          <ReactPaginate
            breakLabel=""
            previousLabel="‹"
            nextLabel="›"
            onPageChange={({ selected }) => onPageChange(selected + 1)}
            pageRangeDisplayed={0}
            marginPagesDisplayed={0}
            pageCount={pageCount}
            forcePage={page - 1}
            containerClassName="pagination-nav"
            previousLinkClassName="nav-btn"
            nextLinkClassName="nav-btn"
            pageLinkClassName="hidden-page"
            breakLinkClassName="hidden-break"
            disabledClassName="nav-disabled"
          />
          <Box
            as="button"
            sx={navBtnStyle}
            onClick={() => onPageChange(pageCount)}
            disabled={page === pageCount}
          >
            »
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}
